import React, { useState, useEffect } from "react";
import axios from "axios";
import { FileText } from "lucide-react";
import { API_URL } from "../config";

// Put this file in src/secure/VaultPicker.js
//
// <VaultPicker open={show} onClose={() => setShow(false)} onSelect={(doc) => ...} />
//
// onSelect receives the same shape the upload pages already build for a browsed file:
//   { file, name, size, date, url, vaultFileId, vaultCid }

const getOwnerEmail = () => {
  const stored = localStorage.getItem("email");
  if (stored) return stored;
  try {
    return JSON.parse(localStorage.getItem("user") || "{}").email || "";
  } catch {
    return "";
  }
};

const formatBytes = (bytes) => {
  if (!bytes) return "0 Bytes";
  const k = 1024;
  const sizes = ["Bytes", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
};

// Record that a flow on this Secure Drive file was completed (e.g. "REQUEST_SENT").
// Never throws.
export async function recordVaultActivity(vaultFileId, action, details = "") {
  try {
    await axios.post(`${API_URL}/api/secure-drive/files/${vaultFileId}/activity`, {
      owner_email: getOwnerEmail(),
      action,
      details,
    });
    return true;
  } catch (err) {
    console.error("Could not save activity to Secure Drive:", err);
    return false;
  }
}

// Save the result of a verification on the Secure Drive file.
// status: "verified" | "failed". Never throws (verification must not break if this fails).
export async function recordVaultVerification(vaultFileId, status) {
  try {
    await axios.post(`${API_URL}/api/secure-drive/files/${vaultFileId}/verification`, {
      owner_email: getOwnerEmail(),
      status,
    });
    return true;
  } catch (err) {
    console.error("Could not save verification status to Secure Drive:", err);
    return false;
  }
}

// purpose: "sign" | "request" | "verify" | "artifact_sign". The backend records that the file was
// selected for this purpose, even if the user never completes the flow.
export default function VaultPicker({ open, onClose, onSelect, purpose }) {
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [picking, setPicking] = useState(null);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;

    const load = async () => {
      setSearch("");
      setLoading(true);
      try {
        const res = await axios.get(`${API_URL}/api/secure-drive/files`, {
          params: { owner_email: getOwnerEmail() },
        });
        if (!cancelled) setFiles(Array.isArray(res.data) ? res.data : []);
      } catch (err) {
        console.error("Failed to load vault files", err);
        alert("Could not load files from Secure Vault.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [open]);

  const handleSelect = async (vf) => {
    setPicking(vf.id);
    try {
      // the backend checks permission, gets the locked file from IPFS and unlocks it
      const res = await axios.get(`${API_URL}/api/secure-drive/download/${vf.id}`, {
        responseType: "blob",
        params: { owner_email: getOwnerEmail(), purpose },
      });
      const file = new File([res.data], vf.file_name, {
        type: vf.file_type || res.data.type || "application/pdf",
      });
      onSelect({
        file,
        name: file.name,
        size: (file.size / 1024 / 1024).toFixed(2) + " MB",
        date: new Date().toLocaleDateString(),
        url: URL.createObjectURL(file),
        vaultFileId: vf.id,
        vaultCid: vf.cid,
      });
      onClose();
    } catch (err) {
      console.error("Failed to fetch vault file", err);
      alert("Could not load this file from Secure Vault.");
    } finally {
      setPicking(null);
    }
  };

  if (!open) return null;

  const filtered = files.filter((f) =>
    (f.file_name || "").toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl w-full max-w-lg max-h-[80vh] flex flex-col shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200">
          <h3 className="text-base font-semibold text-gray-800">Select from Secure Vault</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl leading-none">
            ×
          </button>
        </div>

        <div className="px-5 pt-4">
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search files..."
            className="w-full h-10 px-3 border border-gray-200 rounded-lg text-sm outline-none focus:border-blue-500"
          />
        </div>

        <div className="p-5 overflow-y-auto space-y-3">
          {loading && <p className="text-center text-sm text-gray-400">Loading files...</p>}

          {!loading && filtered.length === 0 && (
            <p className="text-center text-sm text-gray-400">No files found in your vault.</p>
          )}

          {filtered.map((f) => (
            <div
              key={f.id}
              className="flex items-center justify-between border border-gray-200 rounded-xl px-3 py-2 hover:border-blue-300 hover:shadow-sm transition"
            >
              <div className="flex items-center gap-3 min-w-0">
                <div className="bg-red-100 p-2 rounded-lg shrink-0">
                  <FileText className="text-red-500 w-4 h-4" />
                </div>
                <div className="min-w-0">
                  <p className="text-sm text-[#111827] truncate">
                    {f.file_name}
                    {f.source === "signed" && (
                      <span className="ml-2 px-2 py-0.5 text-[10px] rounded-full bg-green-100 text-green-700 font-medium">
                        Signed
                      </span>
                    )}
                    {f.verification_status === "verified" && (
                      <span className="ml-2 px-2 py-0.5 text-[10px] rounded-full bg-emerald-100 text-emerald-700 font-medium">
                        Verified
                      </span>
                    )}
                  </p>
                  <p className="text-xs text-[#4B5563]">
                    {formatBytes(f.file_size || 0)} • v{f.version || 1}
                  </p>
                </div>
              </div>
              <button
                onClick={() => handleSelect(f)}
                disabled={picking === f.id}
                className="px-3 py-1 bg-blue-50 text-blue-600 rounded-md hover:bg-blue-100 transition text-xs font-medium disabled:opacity-50"
              >
                {picking === f.id ? "Loading..." : "Select"}
              </button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}